import { Role } from '@mai-team-amusement/core';
import { LocaleProvider, MaiApp, SystemLayout, useCommonT } from '@mai-team-amusement/ui';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import type { i18n } from 'i18next';
import { RouterProvider, createBrowserRouter, type RouteObject } from 'react-router';
import { ClockQueryKey, clockApi } from './api.js';
import { useClockT } from './i18n/index.js';
import { ClockPage } from './pages/ClockPage.js';

const LOADING_MARGIN = 8;

/** Approvers see pending requests in the tab label; refreshed every few minutes. */
const PENDING_REFRESH_MS = 120_000;

function Layout() {
  const t = useClockT();
  const common = useCommonT();
  const pending = useQuery({
    queryKey: ClockQueryKey.PendingCount,
    queryFn: clockApi.pendingCount,
    refetchInterval: PENDING_REFRESH_MS,
    retry: false,
  });
  const count = pending.data?.count ?? 0;
  return (
    <SystemLayout
      app={MaiApp.Clock}
      navigation={[
        { path: '/', label: t('nav.clock'), minRole: Role.Employee },
        { path: '/records', label: t('nav.records'), minRole: Role.Employee },
        { path: '/requests', label: t('nav.requests'), minRole: Role.Employee },
        {
          path: '/approvals',
          label: count > 0 ? `${t('nav.approvals')} (${String(count)})` : t('nav.approvals'),
          minRole: Role.Manager,
        },
        { path: '/team', label: t('nav.team'), minRole: Role.Manager },
        { path: '/schedules', label: t('nav.schedules'), minRole: Role.Manager },
        { path: '/admin', label: common('system.nav.admin'), minRole: Role.Admin },
      ]}
    />
  );
}

/** Shown while the first page's code loads. */
function RouteLoading() {
  const common = useCommonT();
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: LOADING_MARGIN }}>
      <CircularProgress aria-label={common('status.loading')} />
    </Box>
  );
}

/**
 * The app's routes (also rendered by tests with a memory router). The clock page, which most
 * visits open (often on a phone), is in the main bundle; the other pages load on first use.
 * A new array per router: resolving a lazy route updates its route object.
 */
export const createRoutes = (): RouteObject[] => [
  {
    path: '/',
    Component: Layout,
    HydrateFallback: RouteLoading,
    children: [
      { index: true, Component: ClockPage },
      {
        path: 'records',
        lazy: { Component: async () => (await import('./pages/RecordsPage.js')).RecordsPage },
      },
      {
        path: 'records/:sub/:date',
        lazy: { Component: async () => (await import('./pages/DayPage.js')).DayPage },
      },
      {
        path: 'requests',
        lazy: { Component: async () => (await import('./pages/RequestsPage.js')).RequestsPage },
      },
      {
        path: 'requests/new',
        lazy: {
          Component: async () => (await import('./pages/RequestFormPage.js')).RequestFormPage,
        },
      },
      {
        path: 'approvals',
        lazy: { Component: async () => (await import('./pages/RequestsPage.js')).ApprovalsPage },
      },
      {
        path: 'team',
        lazy: { Component: async () => (await import('./pages/TeamPage.js')).TeamPage },
      },
      {
        path: 'schedules',
        lazy: { Component: async () => (await import('./pages/SchedulesPage.js')).SchedulesPage },
      },
      {
        path: 'admin',
        lazy: { Component: async () => (await import('./pages/AdminPage.js')).AdminPage },
      },
    ],
  },
];

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false } },
});

const router = createBrowserRouter(createRoutes());

export function App({ i18n }: { i18n: i18n }) {
  return (
    <QueryClientProvider client={queryClient}>
      <LocaleProvider i18n={i18n}>
        <RouterProvider router={router} />
      </LocaleProvider>
    </QueryClientProvider>
  );
}
